import { LocationDto } from "@braoshzmvavz/picnube-common";
import { Button, Transition, useMantineTheme } from "@mantine/core";
import React, { useEffect, useRef, useState } from "react";
import { MapRef } from "react-map-gl/maplibre";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { Location } from "../../model/Location";
import BaseMap from "./BaseMap";
import LocationDetail from "./LocationDetail";
import LocationSelector from "./LocationSelector";
import { useMediaQuery } from "@mantine/hooks";
import { Drawer } from "vaul";
import LocationInfo from "./LocationInfo";
import DefaultDrawerTrigger from "./DefaultDrawerTrigger";
import { HiChevronDown, HiChevronRight } from "react-icons/hi";
import useApi from "src/services/useApi";
import { removeLocation } from "src/state/locations/locationSlice";

export enum MapViewMode {
  VIEW,
  SELECT_LOCATION,
}

export default function MapView(props: {
  defaultMode: MapViewMode;
  defaultSelectedLocation?: string;
  onConfirm?: (location: LocationDto) => void;
}) {
  const theme = useMantineTheme();
  const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.xs})`);

  const [isOpen, setIsOpen] = useState(false);

  const locationState = useAppSelector((state) => state.location);

  const [selectedLocation, setSelectedLocation] = useState(null);
  const [isLocationDetailViewEnabled, setLocationDetailViewEnabled] =
    useState(false);

  const mapRef = useRef<MapRef>(null);
  const [mapCenter, setMapCenter] = useState([0, 0]);

  const [defaultLocationLatLong, setDefaultLocationLatLong] = useState([
    9.389839, 50.622884,
  ]);

  const [mapViewMode, setMapViewMode] = useState(
    props.defaultMode ?? MapViewMode.VIEW
  );

  const { deleteLocation } = useApi();
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (!props.defaultSelectedLocation) return;

    const location: Location = locationState.locations.find(
      (location) => location.id === props.defaultSelectedLocation
    );
    if (!location) return;

    setSelectedLocation(location);
    setDefaultLocationLatLong([location.longitude, location.latitude]);
  }, [props.defaultSelectedLocation]);

  const onMapViewportChange = () => {
    const map = mapRef.current;
    if (!map) return;

    const { lat, lng } = map.getCenter();
    setMapCenter([lat, lng]);
  };

  const onDeleteLocation = (location: LocationDto) => {
    console.log("deleteLocation", location);

    deleteLocation(location.id).then((response) => {
      console.log("deleted location " + location.name, response);
      setSelectedLocation(null);
      dispatch(removeLocation(location.id));
    });
  };

  const onSelectLocation = (location: LocationDto, forceSelection = false) => {
    if (isMobile) {
      setSnap(snapPoints[0]);
    }
    if (forceSelection) {
      setSelectedLocation(location);
      return;
    }
    if (isLocationDetailViewEnabled && selectedLocation?.id === location?.id) {
      return;
    }

    if (selectedLocation?.id === location?.id) {
      setSelectedLocation(null);
    } else {
      setSelectedLocation(location);
    }
  };

  const onSelectLocationForDetail = (location: LocationDto) => {
    if (
      !!props?.onConfirm &&
      props.defaultMode === MapViewMode.SELECT_LOCATION
    ) {
      props.onConfirm(location);
      return;
    }
    onSelectLocation(location, true);
    setLocationDetailViewEnabled(true);
  };

  const snapPoints = ["135px", 0.9];
  const [snap, setSnap] = useState<number | string | null>(snapPoints[0]);

  if (isMobile) {
    return (
      <div className="flex flex-col w-full h-full">
        <Transition
          mounted={isLocationDetailViewEnabled}
          transition="fade-left"
          duration={200}
          timingFunction="ease"
        >
          {(transitionStyle) => (
            <div className="flex flex-col w-full h-full absolute z-20 dark:bg-grey-900 bg-grey-100">
              <LocationDetail
                location={selectedLocation}
                style={{ ...transitionStyle }}
                onDeleteLocation={onDeleteLocation}
                closeLocationDetailView={() =>
                  setLocationDetailViewEnabled(false)
                }
              ></LocationDetail>
            </div>
          )}
        </Transition>

        <div className="flex flex-col w-full h-full mb-[120px]">
          <BaseMap
            defaultMode={MapViewMode.VIEW}
            defaultSelectedLocation={selectedLocation?.id}
            selectedLocation={selectedLocation}
            onSelectLocation={onSelectLocation}
            onSelectLocationForDetail={onSelectLocationForDetail}
            onConfirm={props.onConfirm}
          ></BaseMap>
        </div>

        <Drawer.Root
          open={true}
          dismissible={false}
          modal={false}
          snapPoints={snapPoints}
          activeSnapPoint={snap}
          setActiveSnapPoint={setSnap}
        >
          {/* <Drawer.Trigger>
            {selectedLocation != null ? (
              <div>
                <LocationInfo
                  location={selectedLocation}
                  onClick={() => {}}
                  rightIcon={<HiChevronDown />}
                ></LocationInfo>
              </div>
            ) : (
              <div>
                <DefaultDrawerTrigger />
              </div>
            )}
          </Drawer.Trigger> */}
          <Drawer.Portal>
            <Drawer.Overlay className="fixed inset-0 bg-black/40 bottom-drawer-overlay" />
            <Drawer.Content
              className="dark:bg-grey-900 bg-grey-100 flex flex-col rounded-t-[10px] mt-24 
            h-full max-h-[97%] fixed bottom-0 left-0 right-0 outline-none bottom-drawer-content"
            >
              <div aria-hidden className="mb-3" />
              <Drawer.Handle />
              <Drawer.Title />
              <Drawer.Description />
              <Drawer.Close />
              <div className="flex flex-col gap-4 p-2 pt-0 h-full max-h-[85%] overflow-auto">
                {selectedLocation != null ? (
                  <LocationInfo
                    location={selectedLocation}
                    onClick={() => {
                      setIsOpen(false);
                      setLocationDetailViewEnabled(true);
                    }}
                    rightIcon={<HiChevronRight />}
                  ></LocationInfo>
                ) : (
                  ""
                )}
                <LocationSelector
                  onSearchValueChange={(searchValue) => {
                    // setSnap(snapPoints[searchValue.length > 0 ? 1 : 0]);
                  }}
                  selectedLocation={selectedLocation}
                  onLocationSelected={onSelectLocation}
                  onLocationSelectedForDetail={onSelectLocationForDetail}
                  isMobileAndDropdown={true}
                  onFocus={() => setSnap(snapPoints[1])}
                  onTextNotEmpty={() => setSnap(snapPoints[1])}
                />
              </div>
            </Drawer.Content>
          </Drawer.Portal>
        </Drawer.Root>
      </div>
    );
  }

  return (
    <div className="flex flex-row w-full h-full overflow-hidden gap-2">
      <div className="w-96 card-base">
        <LocationSelector
          selectedLocation={selectedLocation}
          onLocationSelected={onSelectLocation}
          onLocationSelectedForDetail={onSelectLocationForDetail}
        />
      </div>
      <div className={`flex w-full h-full relative card-base ${!isMobile ? "overflow-hidden" : ""}`}>
        <Transition
          mounted={isLocationDetailViewEnabled}
          transition="fade-left"
          duration={200}
          timingFunction="ease"
        >
          {(transitionStyle) => (
            <div className="flex flex-col w-full h-full absolute  z-20 dark:bg-grey-900 bg-grey-100">
              <LocationDetail
                location={selectedLocation}
                style={{ ...transitionStyle }}
                onDeleteLocation={onDeleteLocation}
                closeLocationDetailView={() =>
                  setLocationDetailViewEnabled(false)
                }
              ></LocationDetail>
            </div>
          )}
        </Transition>
        <BaseMap
          defaultMode={mapViewMode}
          defaultSelectedLocation={selectedLocation?.id}
          selectedLocation={selectedLocation}
          onSelectLocation={onSelectLocation}
          onSelectLocationForDetail={onSelectLocationForDetail}
          projection={'globe'}
          onConfirm={props.onConfirm}
        ></BaseMap>
      </div>
    </div>
  );
}
